import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  auditService,
  Methodology,
  TestEvaluation,
  RecordableVerdict,
} from '@services/auditService';
import MethodologyEditor from './MethodologyEditor';
import { toDisplayError } from '@utils/apiError';

/**
 * REV-359: shared inline verdict-recording form.
 *
 * Extracted from CriterionDetailPage so both that page and the
 * REV-56/REV-358 ManualChecklistPage can edit a TestEvaluation in place
 * without duplicating the form logic. Behavior matches the original
 * inline form exactly:
 *
 *   - Verdict select (Pass / Fail / N/A — never "not_tested" since you
 *     can't choose to "un-test" something via this surface)
 *   - Free-text note
 *   - Optional MethodologyEditor (tools / techniques / AT / steps)
 *   - Cancel + Save buttons
 *
 * The methodology semantics match the prior implementation: an empty
 * MethodologyEditor object sends `null` to the backend, which clears
 * any prior methodology. A populated editor sends the populated value.
 * `undefined` would have left the stored value alone — we want explicit
 * clear semantics here.
 */

interface Props {
  /** The TestEvaluation being edited. Provides initial values + the id used for the API call. */
  te: TestEvaluation;
  /** Owning organization for the recordVerdict call. */
  orgId: string;
  /** Called after a successful save. The page passes its refresh trigger here. */
  onSaved: (updated: TestEvaluation) => void;
  /** Called when the reviewer cancels. The form closes — caller controls visibility. */
  onCancel: () => void;
}

// Pre-fill from the TE so reviewers can edit existing notes/methodology
// without re-typing. "not_tested" maps to empty so the Save button stays
// disabled until they pick a real verdict, and since REV-1072 so does
// "needs_review": that is the engine's holding state rather than anyone's
// judgement, it is not one of the options below, and the API refuses it on
// write. Pre-filling it would put an unsaveable value in the control and
// read as though a verdict had already been recorded. The NOTE still
// pre-fills, because what the scan said is exactly what the reviewer is
// being asked to confirm.
const HELD_VERDICTS = ['not_tested', 'needs_review'];

export default function TestEvaluationVerdictForm({ te, orgId, onSaved, onCancel }: Props) {
  const { t } = useTranslation();

  const [verdict, setVerdict] = useState<string>(
    HELD_VERDICTS.includes(te.verdict) ? '' : te.verdict
  );
  const [note, setNote] = useState<string>(te.verdict_note || '');
  const [methodology, setMethodology] = useState<Methodology>(te.methodology || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!verdict || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Same semantics as the original: empty methodology → null (explicit
      // clear); populated methodology → the populated value.
      const methodologyHasContent = Object.values(methodology).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );
      const updated = await auditService.recordVerdict(orgId, te.id, {
        verdict: verdict as RecordableVerdict,
        verdict_note: note || undefined,
        methodology: methodologyHasContent ? methodology : null,
      });
      onSaved(updated);
    } catch (err) {
      setError(toDisplayError(err, t('verdictForm.error', 'Failed to save verdict')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minWidth: 420,
        textAlign: 'left',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={verdict}
            onChange={(e) => setVerdict(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': t('verdictForm.verdictAria', 'Verdict') }}
          >
            <MenuItem value="" disabled>
              {t('verdictForm.verdictPlaceholder', 'Verdict')}
            </MenuItem>
            <MenuItem value="pass">{t('verdictForm.pass', 'Pass')}</MenuItem>
            <MenuItem value="fail">{t('verdictForm.fail', 'Fail')}</MenuItem>
            <MenuItem value="not_applicable">{t('verdictForm.na', 'N/A')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder={t('verdictForm.notePlaceholder', 'Note (optional)')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 150 }}
          inputProps={{ 'aria-label': t('verdictForm.noteAria', 'Verdict note') }}
        />
      </Box>

      <MethodologyEditor value={methodology} onChange={setMethodology} disabled={saving} />

      {error && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: 14 }}>
          {error}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} disabled={saving}>
          {t('verdictForm.cancel', 'Cancel')}
        </Button>
        <Button size="small" variant="contained" disabled={!verdict || saving} onClick={handleSave}>
          {saving ? <CircularProgress size={16} /> : t('verdictForm.save', 'Save')}
        </Button>
      </Box>
    </Box>
  );
}
